/**
 * Privacy & Compliance card for the account / subscription page.
 *
 * Combines the two complementary privacy controls:
 *
 *  1. **Zero Data Retention (provider-side).** Restricts every AI call
 *     this account makes to providers we can contractually guarantee
 *     won't log or train on the data, with US data residency enforced.
 *     See `docs/ai-agent-guide/sections/09-zero-data-retention.md`.
 *
 *  2. **Run history retention (storage-side).** Controls how long *we*
 *     keep run-history rows (function runs and chain executions) in
 *     our own database. "Do not store" is the strictest tier and pairs
 *     naturally with ZDR for healthcare/BAA customers.
 *
 * Defense-in-depth lives in the backend; this card is the user-facing
 * surface + audit trail of who flipped what and when.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { useAuth } from '../contexts/AuthContext';
import { getConfig } from '../services/config';
import { invalidateZdrStatus } from '../hooks/useZdrStatus';
import {
  RETENTION_OPTIONS,
  RetentionDays,
  describeRetention,
  retentionToSentinel,
  sentinelToRetention,
} from '../config/dataRetentionOptions';

interface ZdrStatus {
  enabled: boolean;
  set_at: string | null;
  set_by: string | null;
  /** Data-region preference (`fastest` | `us`) — second axis of ZDR. */
  region_mode: string;
  /** Feature flag: when false the region selector is "coming soon" and
   *  the backend ignores any stored `us` value (routes as `fastest`). */
  region_selection_enabled: boolean;
}

const ZDR_REGION_FASTEST = 'fastest';
const ZDR_REGION_US = 'us';

interface RetentionStatus {
  days: RetentionDays;
  /** Effective retention applied to chatbot/thread runs after the
   *  chatbot floor is applied. `null` matches the Forever tier. */
  chatbot_effective_days: number | null;
  /** System-imposed minimum kept for chatbot/thread runs even on the
   *  "Do not store" tier — usually 1 day. */
  chatbot_floor_days: number;
  /** Human-readable description of the chatbot retention policy. */
  chatbot_description: string;
  set_at: string | null;
  set_by: string | null;
}

interface PrivacyComplianceCardProps {
  /** Disable the toggles (e.g. free tier). The card still renders so users
   *  see the feature exists. */
  disabled?: boolean;
  /** Tooltip-style message shown when `disabled` is true (e.g. "Upgrade
   *  to enable"). */
  disabledReason?: string;
  /** Fired whenever the ZDR state successfully flips. Parents can use
   *  this to refresh model selectors or banners. */
  onChange?: (enabled: boolean) => void;
  /** Fired whenever the retention setting successfully changes. */
  onRetentionChange?: (days: RetentionDays) => void;
}

const PrivacyComplianceCard: React.FC<PrivacyComplianceCardProps> = ({
  disabled = false,
  disabledReason,
  onChange,
  onRetentionChange,
}) => {
  const { getIdToken, isImpersonating } = useAuth();
  const [loading, setLoading] = useState(true);
  const [savingZdr, setSavingZdr] = useState(false);
  const [savingRegion, setSavingRegion] = useState(false);
  const [savingRetention, setSavingRetention] = useState(false);
  const [zdr, setZdr] = useState<ZdrStatus | null>(null);
  const [retention, setRetention] = useState<RetentionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ZDR confirm dialog state
  const [zdrConfirmOpen, setZdrConfirmOpen] = useState(false);
  const [pendingZdr, setPendingZdr] = useState<boolean | null>(null);
  const [zdrAcknowledged, setZdrAcknowledged] = useState(false);

  // Retention confirm dialog state
  const [retentionConfirmOpen, setRetentionConfirmOpen] = useState(false);
  const [pendingRetention, setPendingRetention] = useState<RetentionDays | undefined>(undefined);
  const [retentionAcknowledged, setRetentionAcknowledged] = useState(false);

  const apiBase = useMemo(() => getConfig().apiBaseUrl, []);

  // Defensive client-side timeout. The backend should always respond
  // promptly to these endpoints, but a misbehaving middleware (or any
  // future bug that makes the server hang mid-request) would otherwise
  // leave this dialog spinning forever with the Save button greyed out.
  // 30s is generous for a single PATCH/GET against our control plane.
  const REQUEST_TIMEOUT_MS = 30_000;
  const fetchWithTimeout = async (
    input: RequestInfo,
    init: RequestInit = {},
    timeoutMs: number = REQUEST_TIMEOUT_MS,
  ): Promise<Response> => {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      return await fetch(input, { ...init, signal: ctrl.signal });
    } catch (e) {
      // Surface a friendlier message than the default "AbortError".
      if ((e as Error)?.name === 'AbortError') {
        throw new Error(
          `Request timed out after ${Math.round(timeoutMs / 1000)}s. ` +
            'The server may be overloaded — please try again.',
        );
      }
      throw e;
    } finally {
      window.clearTimeout(timer);
    }
  };

  const fetchAll = async () => {
    try {
      const token = await getIdToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      const [zdrResp, retResp] = await Promise.all([
        fetchWithTimeout(`${apiBase}/api/v2/customers/zero-data-retention`, { headers }),
        fetchWithTimeout(`${apiBase}/api/v2/customers/data-retention`, { headers }),
      ]);
      if (!zdrResp.ok) throw new Error(`ZDR fetch failed: HTTP ${zdrResp.status}`);
      if (!retResp.ok) throw new Error(`Retention fetch failed: HTTP ${retResp.status}`);

      const zdrBody = await zdrResp.json();
      const zdrData = zdrBody?.data ?? zdrBody;
      setZdr({
        enabled: !!zdrData.enabled,
        set_at: zdrData.set_at ?? null,
        set_by: zdrData.set_by ?? null,
        region_mode: zdrData.region_mode === ZDR_REGION_US ? ZDR_REGION_US : ZDR_REGION_FASTEST,
        region_selection_enabled: !!zdrData.region_selection_enabled,
      });

      const retBody = await retResp.json();
      const retData = retBody?.data ?? retBody;
      setRetention({
        days: retData.days === undefined ? null : retData.days,
        chatbot_effective_days:
          retData.chatbot_effective_days === undefined
            ? null
            : retData.chatbot_effective_days,
        chatbot_floor_days: retData.chatbot_floor_days ?? 1,
        chatbot_description: retData.chatbot_description ?? '',
        set_at: retData.set_at ?? null,
        set_by: retData.set_by ?? null,
      });
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Failed to load privacy settings: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Don't fetch when the card is locked — the user can't change
    // anything anyway, and we'd just create unnecessary load (and
    // surface noisy errors to customers on plans without the feature).
    if (disabled) {
      setLoading(false);
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  // -- ZDR toggle ----------------------------------------------------

  const handleZdrToggleClick = (next: boolean) => {
    setPendingZdr(next);
    setZdrAcknowledged(false);
    setZdrConfirmOpen(true);
  };

  const handleZdrConfirm = async () => {
    if (pendingZdr === null) return;
    setSavingZdr(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const resp = await fetchWithTimeout(`${apiBase}/api/v2/customers/zero-data-retention`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: pendingZdr }),
      });
      if (!resp.ok) {
        let detail: string;
        try {
          const body = await resp.json();
          detail = body?.detail || body?.message || JSON.stringify(body);
        } catch {
          detail = await resp.text();
        }
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const body = await resp.json();
      const data = body?.data ?? body;
      const newStatus: ZdrStatus = {
        enabled: !!data.enabled,
        set_at: data.set_at ?? null,
        set_by: data.set_by ?? null,
        region_mode: data.region_mode === ZDR_REGION_US ? ZDR_REGION_US : ZDR_REGION_FASTEST,
        region_selection_enabled: !!data.region_selection_enabled,
      };
      setZdr(newStatus);
      setZdrConfirmOpen(false);
      setPendingZdr(null);
      invalidateZdrStatus();
      if (onChange) onChange(newStatus.enabled);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Failed to update ZDR: ${msg}`);
    } finally {
      setSavingZdr(false);
    }
  };

  const handleZdrCancel = () => {
    setZdrConfirmOpen(false);
    setPendingZdr(null);
    setZdrAcknowledged(false);
  };

  // -- Data-region selector (second axis, behind feature flag) -------

  const handleRegionChange = async (e: SelectChangeEvent<string>) => {
    const next = e.target.value === ZDR_REGION_US ? ZDR_REGION_US : ZDR_REGION_FASTEST;
    if (!zdr || zdr.region_mode === next) return;
    setSavingRegion(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const resp = await fetchWithTimeout(`${apiBase}/api/v2/customers/zero-data-retention`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ region_mode: next }),
      });
      if (!resp.ok) {
        let detail: string;
        try {
          const body = await resp.json();
          detail = body?.detail?.reason || body?.detail || body?.message || JSON.stringify(body);
        } catch {
          detail = await resp.text();
        }
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const body = await resp.json();
      const data = body?.data ?? body;
      setZdr({
        enabled: !!data.enabled,
        set_at: data.set_at ?? null,
        set_by: data.set_by ?? null,
        region_mode: data.region_mode === ZDR_REGION_US ? ZDR_REGION_US : ZDR_REGION_FASTEST,
        region_selection_enabled: !!data.region_selection_enabled,
      });
      invalidateZdrStatus();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Failed to update data region: ${msg}`);
    } finally {
      setSavingRegion(false);
    }
  };

  // -- Retention selector --------------------------------------------

  const handleRetentionSelect = (e: SelectChangeEvent<string>) => {
    const next = sentinelToRetention(e.target.value);
    if (retention?.days === next) return;
    setPendingRetention(next);
    setRetentionAcknowledged(false);
    setRetentionConfirmOpen(true);
  };

  const handleRetentionConfirm = async () => {
    if (pendingRetention === undefined) return;
    setSavingRetention(true);
    setError(null);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');
      const resp = await fetchWithTimeout(`${apiBase}/api/v2/customers/data-retention`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ days: pendingRetention }),
      });
      if (!resp.ok) {
        let detail: string;
        try {
          const body = await resp.json();
          detail = body?.detail || body?.message || JSON.stringify(body);
        } catch {
          detail = await resp.text();
        }
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const body = await resp.json();
      const data = body?.data ?? body;
      const newStatus: RetentionStatus = {
        days: data.days === undefined ? null : data.days,
        chatbot_effective_days:
          data.chatbot_effective_days === undefined
            ? null
            : data.chatbot_effective_days,
        chatbot_floor_days: data.chatbot_floor_days ?? 1,
        chatbot_description: data.chatbot_description ?? '',
        set_at: data.set_at ?? null,
        set_by: data.set_by ?? null,
      };
      setRetention(newStatus);
      setRetentionConfirmOpen(false);
      setPendingRetention(undefined);
      if (onRetentionChange) onRetentionChange(newStatus.days);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(`Failed to update retention: ${msg}`);
    } finally {
      setSavingRetention(false);
    }
  };

  const handleRetentionCancel = () => {
    setRetentionConfirmOpen(false);
    setPendingRetention(undefined);
    setRetentionAcknowledged(false);
  };

  // -- Render --------------------------------------------------------

  const zdrEnabled = !!zdr?.enabled;
  const currentRetention: RetentionDays = retention ? retention.days : null;
  const pendingOption = RETENTION_OPTIONS.find((o) => o.value === pendingRetention);

  // The retention=0 option is the only one that needs a hard
  // confirmation — picking it deletes nothing immediately, but it
  // does prevent any new history from being written. All other
  // changes (e.g. forever -> 30 days) trigger eventual deletion via
  // the daily cron, which is also worth flagging.
  const isRetentionDestructive =
    pendingRetention === 0 ||
    (typeof pendingRetention === 'number' &&
      typeof currentRetention === 'number' &&
      pendingRetention < currentRetention) ||
    (typeof pendingRetention === 'number' && currentRetention === null);

  return (
    <Card
      sx={{
        mb: 3,
        // Subtle "locked" treatment: a soft tint + dashed border so the
        // card visibly reads as gated rather than just inactive. We still
        // show the full descriptive copy below — we just hide the
        // interactive controls.
        ...(disabled
          ? {
              bgcolor: 'action.hover',
              border: '1px dashed',
              borderColor: 'divider',
            }
          : {}),
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {disabled ? (
            <LockIcon color="disabled" />
          ) : (
            <ShieldOutlinedIcon color={zdrEnabled ? 'success' : 'action'} />
          )}
          <Typography
            variant="h6"
            color={disabled ? 'text.secondary' : 'text.primary'}
          >
            Privacy &amp; Compliance
          </Typography>
          {disabled && (
            <Box
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'grey.300',
                color: 'text.secondary',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              LOCKED
            </Box>
          )}
          {!disabled && zdrEnabled && (
            <Box
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'success.main',
                color: 'success.contrastText',
                fontSize: 12,
              }}
            >
              ZDR ON
            </Box>
          )}
          {!disabled && currentRetention === 0 && (
            <Box
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'info.main',
                color: 'info.contrastText',
                fontSize: 12,
              }}
            >
              NO-STORE
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {disabled && disabledReason && (
          <Alert severity="warning" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }}>
            {disabledReason}
          </Alert>
        )}
        {isImpersonating && (
          // The customer-side ZDR / retention PATCH endpoints are
          // blocked by the server-side block_during_impersonation
          // dependency. Show the card in read-only mode rather than
          // letting the operator toggle a switch and watch it
          // silently 403. Admin overrides for these fields live in
          // the Customer Hub.
          <Alert severity="info" sx={{ mb: 2 }}>
            You're viewing this customer's privacy settings while
            impersonating. Edits are disabled — use the Customer Hub
            to change ZDR or data-retention as an administrator.
          </Alert>
        )}

        {loading && !disabled ? (
          <CircularProgress size={20} />
        ) : (
          <Stack spacing={3}>
            {/* ----- Zero Data Retention (provider-side) ---------- */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ShieldOutlinedIcon fontSize="small" color={zdrEnabled && !disabled ? 'success' : 'action'} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Zero Data Retention (No-Train Mode)
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                Restricts every AI call this account makes to providers we can
                contractually guarantee won&apos;t log or train on your payloads.
                <strong> Enabling ZDR does not pin your data to the US</strong> —
                data residency is a separate control (see <em>Data Region</em>
                below).
              </Typography>
              <Box component="ul" sx={{ pl: 3, mb: 1.5, color: 'text.secondary', fontSize: 14 }}>
                <li>Routes only to Google Vertex AI and OpenAI GPT models with request storage disabled (both covered by no-train contracts). With US data residency, only US-pinned Vertex AI models.</li>
                <li>Disables xAI / Whisper / Veo / OpenAI image paths — any tool requiring them returns 503 instead of leaking data.</li>
                <li>Per-call audit log retained for compliance.</li>
                <li><strong>Data region (US pinning) is a separate setting</strong> — see <em>Data Region</em> below.</li>
              </Box>
              {!disabled && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={zdrEnabled}
                        disabled={savingZdr || isImpersonating}
                        onChange={(e) => handleZdrToggleClick(e.target.checked)}
                      />
                    }
                    label="Enable Zero Data Retention"
                  />
                  {zdr?.set_at && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Last changed {new Date(zdr.set_at).toLocaleString()}
                      {zdr.set_by ? ` by ${zdr.set_by}` : ''}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            <Divider />

            {/* ----- Data Region (residency axis — separate from ZDR) -----
                Always shown so customers understand residency is its own
                control. Interaction is gated behind the backend
                `region_selection_enabled` flag (ENABLE_ZDR_REGION_SELECTION),
                which is OFF for now — so the selector is rendered DISABLED
                ("not yet available") and everyone effectively runs on
                `fastest` (global). We never promise US residency from ZDR
                alone. */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PublicOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Data Region (Residency)
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                Controls <em>where</em> your AI calls are processed. This is a
                <strong> separate control from Zero Data Retention</strong> —
                turning on ZDR restricts <em>which</em> models run, but does
                <strong> not</strong> pin your data to the US on its own.
              </Typography>
              <FormControl
                fullWidth
                size="small"
                disabled={!zdr?.region_selection_enabled || !zdrEnabled || savingRegion || isImpersonating}
              >
                <InputLabel id="zdr-region-label">Data region</InputLabel>
                <Select
                  labelId="zdr-region-label"
                  label="Data region"
                  value={
                    zdr?.region_selection_enabled
                      ? (zdr?.region_mode ?? ZDR_REGION_FASTEST)
                      : ZDR_REGION_FASTEST
                  }
                  onChange={handleRegionChange}
                >
                  <MenuItem value={ZDR_REGION_FASTEST}>
                    <Box>
                      <Typography variant="body2">Fastest available — global (default)</Typography>
                      <Typography variant="caption" color="textSecondary">
                        No region constraint — routed for best availability. Data may be
                        processed outside the US.
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value={ZDR_REGION_US}>
                    <Box>
                      <Typography variant="body2">United States only</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Pins processing to US regions for data residency. May be slower or
                        throttled at peak times.
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              {!zdr?.region_selection_enabled ? (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                  <strong>Not yet available.</strong> Region pinning is coming
                  soon. For now every account — including those with ZDR
                  enabled — is processed as <strong>Fastest (global)</strong>.
                </Typography>
              ) : !zdrEnabled ? (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                  Enable Zero Data Retention above to choose a data region.
                </Typography>
              ) : (
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                  {savingRegion
                    ? 'Saving…'
                    : `Currently processing as ${
                        zdr?.region_mode === ZDR_REGION_US
                          ? 'United States only'
                          : 'Fastest (global)'
                      }.`}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* ----- Run history retention (storage-side) -------- */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HistoryIcon fontSize="small" color="action" />
                <Typography variant="subtitle1" fontWeight={600}>
                  Run History Retention
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1.5 }}>
                Controls how long <em>we</em> keep records of your function and
                chain runs in our database. Run history powers your run/chain
                history pages — pick a shorter window or “Do not store” to
                tighten privacy at the cost of looking back at past runs.
              </Typography>
              {!disabled ? (
                <>
                  <FormControl fullWidth size="small" disabled={savingRetention || isImpersonating}>
                    <InputLabel id="retention-label">Retention</InputLabel>
                    <Select
                      labelId="retention-label"
                      label="Retention"
                      value={retentionToSentinel(currentRetention)}
                      onChange={handleRetentionSelect}
                    >
                      {RETENTION_OPTIONS.map((opt) => (
                        <MenuItem key={String(opt.value)} value={retentionToSentinel(opt.value)}>
                          <Box>
                            <Typography variant="body2" fontWeight={opt.emphasize ? 600 : 400}>
                              {opt.label}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {opt.description}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {retention?.set_at && (
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                      Currently retaining <strong>{describeRetention(currentRetention)}</strong>.
                      Last changed {new Date(retention.set_at).toLocaleString()}
                      {retention.set_by ? ` by ${retention.set_by}` : ''}.
                    </Typography>
                  )}
                  {/* Always-visible note about the chatbot exemption.
                      Chatbot/thread runs are how the LLM remembers prior
                      turns within a conversation, so deleting them
                      mid-thread breaks the product. We surface the
                      effective policy here so customers aren't
                      surprised by the difference between this dropdown
                      and what actually gets purged for thread runs. */}
                  {retention && (
                    <Alert
                      severity="info"
                      icon={<HistoryIcon fontSize="inherit" />}
                      sx={{ mt: 1.5 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Chatbot conversations are handled separately
                      </Typography>
                      <Typography variant="body2">
                        Multi-turn chatbots use prior runs as memory. To keep them
                        working, threaded conversation runs follow a{' '}
                        <strong>per-thread sliding window</strong>:{' '}
                        {retention.chatbot_description ||
                          (currentRetention === 0
                            ? `${retention.chatbot_floor_days} day rolling window`
                            : `${retention.chatbot_effective_days ?? '∞'} days from each thread's last activity`)}
                        . An active conversation never loses its early turns
                        mid-chat.
                      </Typography>
                      {currentRetention === 0 && (
                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                          If you need <em>strict</em> zero-storage (e.g. for
                          HIPAA / BAA), disable thread mode in your function
                          definitions in addition to selecting “Do not store”.
                          With thread mode off, no chatbot history is written.
                        </Typography>
                      )}
                    </Alert>
                  )}
                </>
              ) : (
                // Locked preview — show the option set as a read-only
                // bullet list so customers can see what they'd unlock.
                <Box component="ul" sx={{ pl: 3, mb: 0, color: 'text.secondary', fontSize: 14 }}>
                  <li><strong>Forever</strong> — keep all history (default).</li>
                  <li><strong>Do not store</strong> — never write run history; pairs with ZDR for the strictest privacy. (Chatbot conversations get a 1-day rolling window so multi-turn chats keep working.)</li>
                  <li><strong>7, 14, 30, 60, 180, 365 days</strong> — auto-purge older history daily. Chatbot threads use a sliding window from last activity so active conversations stay intact.</li>
                </Box>
              )}
              {!disabled && currentRetention === 0 && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  In “Do not store” mode, the input and output of single-shot
                  function runs and chain steps are never written (only usage
                  is kept, for billing), chain results are removed once picked
                  up or after an hour, and older content is removed on the
                  next purge. Chatbot threads keep a{' '}
                  <strong>{retention?.chatbot_floor_days ?? 1}-day</strong>{' '}
                  rolling memory so multi-turn conversations still work.
                </Alert>
              )}
            </Box>
          </Stack>
        )}
      </CardContent>

      {/* ----- ZDR confirm dialog --------------------------------- */}
      <Dialog open={zdrConfirmOpen} onClose={handleZdrCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pendingZdr ? 'Enable Zero Data Retention?' : 'Disable Zero Data Retention?'}
        </DialogTitle>
        <DialogContent>
          {pendingZdr ? (
            <DialogContentText component="div">
              <p>
                Turning ZDR <strong>ON</strong> will restrict every AI call your
                account makes to providers covered by our no-train contract.
                It does <strong>not</strong> change where your data is processed —
                data region stays <strong>Fastest (global)</strong> for now
                (US pinning is a separate setting, coming soon).
              </p>
              <p>This means:</p>
              <ul>
                <li>Tools that rely on xAI, Whisper, Veo, or OpenAI image generation will return 503 errors instead of running.</li>
                <li>Any saved per-route model overrides pointing to non-eligible models will be silently substituted.</li>
                <li>Your billed credit usage will not change — only routing.</li>
              </ul>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={zdrAcknowledged}
                    onChange={(e) => setZdrAcknowledged(e.target.checked)}
                  />
                }
                label="I understand and confirm I want to enable Zero Data Retention for this account."
              />
            </DialogContentText>
          ) : (
            <DialogContentText component="div">
              <p>
                Turning ZDR <strong>OFF</strong> means future AI calls may be
                routed to providers outside our no-train contract (xAI, Whisper,
                Veo, OpenAI image generation). Data region is a separate setting and
                is unaffected.
              </p>
              <p>Are you sure you want to disable it?</p>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={zdrAcknowledged}
                    onChange={(e) => setZdrAcknowledged(e.target.checked)}
                  />
                }
                label="I understand and confirm I want to disable Zero Data Retention."
              />
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleZdrCancel} disabled={savingZdr}>Cancel</Button>
          <Button
            onClick={handleZdrConfirm}
            color={pendingZdr ? 'success' : 'warning'}
            variant="contained"
            disabled={!zdrAcknowledged || savingZdr}
          >
            {savingZdr ? 'Saving…' : pendingZdr ? 'Enable ZDR' : 'Disable ZDR'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ----- Retention confirm dialog --------------------------- */}
      <Dialog open={retentionConfirmOpen} onClose={handleRetentionCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {pendingRetention === 0
            ? 'Disable run history storage?'
            : `Change run history retention to ${pendingOption?.label}?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {pendingRetention === 0 ? (
              <>
                <p>
                  Switching to <strong>Do not store</strong> means we will stop
                  writing single-shot run inputs and outputs to the database immediately.
                  Function and chain runs still execute normally, but:
                </p>
                <ul>
                  <li>Run history shows only usage (model, tokens, timing) for non-chat runs, marked “Content not stored”.</li>
                  <li>Chain results are kept only until they are picked up, or for at most an hour.</li>
                  <li>The content of existing single-shot history rows, and their stored files, will be removed by the daily cron purge.</li>
                  <li>Per-call billing/audit logs are still emitted — they are server-side and contain no payload.</li>
                </ul>
                <Alert severity="warning" sx={{ mt: 1, mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Chatbots are NOT zeroed out
                  </Typography>
                  <Typography variant="body2">
                    Multi-turn chatbots rebuild their memory from prior run rows
                    on each turn — deleting those rows would make the bot
                    amnesiac. Threaded conversation runs are kept on a{' '}
                    <strong>
                      {retention?.chatbot_floor_days ?? 1}-day rolling, per-thread
                      sliding window
                    </strong>{' '}
                    even on the “Do not store” tier. A whole thread is purged
                    only after it has been silent for that long.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    If you need <em>strict</em> zero-storage (e.g. for
                    HIPAA/BAA): also turn off thread mode in your function
                    definitions. With thread mode off, no chatbot history is
                    written and the bot becomes single-turn.
                  </Typography>
                </Alert>
              </>
            ) : isRetentionDestructive ? (
              <>
                <p>
                  Switching to <strong>{pendingOption?.label}</strong>{' '}
                  ({pendingOption?.description.replace(/^./, (c) => c.toLowerCase())})
                  shortens your retention window. The next time the daily purge
                  job runs, it will delete single-shot run history older than
                  the new cutoff.
                </p>
                <p>
                  Chatbot/threaded conversations use a{' '}
                  <strong>per-thread sliding window</strong> — a thread is only
                  deleted after it has been silent for the new window length,
                  so an active conversation never loses its early turns.
                </p>
                <p>This change cannot be undone — once rows are deleted, they're gone.</p>
              </>
            ) : (
              <p>
                Switching retention to <strong>{pendingOption?.label}</strong>.
                Lengthening retention only affects future runs — already-deleted
                history cannot be restored.
              </p>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={retentionAcknowledged}
                  onChange={(e) => setRetentionAcknowledged(e.target.checked)}
                />
              }
              label="I understand and want to apply this retention setting."
            />
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRetentionCancel} disabled={savingRetention}>Cancel</Button>
          <Button
            onClick={handleRetentionConfirm}
            color={isRetentionDestructive ? 'warning' : 'primary'}
            variant="contained"
            disabled={!retentionAcknowledged || savingRetention}
          >
            {savingRetention ? 'Saving…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PrivacyComplianceCard;
